import { useState, useCallback } from 'react'
import { calendarFeedApi, CalendarFeedStatus } from '@/services/api/calendarFeed'
import { rebaseFeedUrl } from '@/utils/feedUrl'

function maskToken(token: string): string {
  if (token.length < 16) {
    return token.length > 8 ? token.slice(0, 4) + '...' + token.slice(-4) : '***'
  }
  return token.slice(0, 8) + '...' + token.slice(-4)
}

export function useCalendarFeed() {
  const [status, setStatus] = useState<CalendarFeedStatus | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const fetchStatus = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await calendarFeedApi.getStatus()
      // calendar-v2 (3.1): Server-URL (BACKEND_URL, Dev localhost) auf die
      // tatsächlich erreichbare Basis re-basen; calendar-v4: in Dev auf die
      // LAN-IP des Backend-Hosts, wenn die Seite per localhost geöffnet ist.
      // calendar-v5: feed_url bleibt origin-basiert (funktioniert auf dem
      // gleichen Gerät, z.B. macOS Calendar-Abo über webcal://localhost);
      // nur der QR-Code nutzt die LAN-IP (qr_url) für andere Geräte.
      setStatus(data?.feed_url ? {
        ...data,
        feed_url: rebaseFeedUrl(data.feed_url),
        qr_url: rebaseFeedUrl(data.feed_url, data.machine_ip),
      } : data)
    } catch (e: any) {
      console.error('[CalendarFeed] Status fetch error:', e)
      setError(e?.detail || 'Status konnte nicht geladen werden')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const subscribe = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await calendarFeedApi.subscribe()
      const rebasedUrl = rebaseFeedUrl(data.feed_url)
      const qrUrl = rebaseFeedUrl(data.feed_url, data.machine_ip)
      setStatus({
        enabled: data.enabled,
        token: maskToken(data.token),
        feed_url: rebasedUrl,
        qr_url: qrUrl,
        last_accessed_at: null,
        role: 'both',
        machine_ip: data.machine_ip,
      })
      setMessage('Kalender-Feed erstellt')
      return { ...data, feed_url: rebasedUrl ?? data.feed_url, qr_url: qrUrl ?? data.feed_url }
    } catch (e: any) {
      console.error('[CalendarFeed] Subscribe error:', e)
      setError(e?.detail || 'Feed konnte nicht erstellt werden')
      throw e
    } finally {
      setIsLoading(false)
    }
  }, [])

  const resetToken = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await calendarFeedApi.resetToken()
      const rebasedUrl = rebaseFeedUrl(data.feed_url)
      const qrUrl = rebaseFeedUrl(data.feed_url, data.machine_ip)
      setStatus({
        enabled: data.enabled,
        token: maskToken(data.token),
        feed_url: rebasedUrl,
        qr_url: qrUrl,
        last_accessed_at: null,
        role: 'both',
        machine_ip: data.machine_ip,
      })
      setMessage('Token zurückgesetzt')
      return { ...data, feed_url: rebasedUrl ?? data.feed_url, qr_url: qrUrl ?? data.feed_url }
    } catch (e: any) {
      console.error('[CalendarFeed] Reset token error:', e)
      setError(e?.detail || 'Token konnte nicht zurückgesetzt werden')
      throw e
    } finally {
      setIsLoading(false)
    }
  }, [])

  const toggle = useCallback(async (enabled: boolean) => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await calendarFeedApi.toggle(enabled)
      setStatus(data?.feed_url ? {
        ...data,
        feed_url: rebaseFeedUrl(data.feed_url),
        qr_url: rebaseFeedUrl(data.feed_url, data.machine_ip),
      } : data)
      setMessage(enabled ? 'Kalender-Feed aktiviert' : 'Kalender-Feed deaktiviert')
    } catch (e: any) {
      console.error('[CalendarFeed] Toggle error:', e)
      setError(e?.detail || 'Feed konnte nicht geändert werden')
      throw e
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    status,
    isLoading,
    error,
    message,
    setMessage,
    fetchStatus,
    subscribe,
    resetToken,
    toggle,
  }
}